import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faWarehouse } from '@fortawesome/free-solid-svg-icons';
import { Progress } from 'reactstrap';
import { withRouter } from 'react-router-dom';
import {
    createDataSource, createGridOptions, DataGrid, indexCellRenderer, LinkCellRenderer, TextFilterDefaults, IconCellRenderer,
    LinkIconRenderer,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import CommonContext, { agGridConstants, ApiRoutes, AppNavPaths } from '../Common';
import { PageHeading, PageWrap } from '../common/forms/FormElements';
import { util } from '../Util';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import authService from '../api-authorization/AuthorizeService';
import { ManageTenant } from '../tenant/ManageTenant';
import { ManageTenantTheme } from '../tenant/ManageTenantTheme';

class TenantIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.companyFormRef = React.createRef();
        this.tenantThemeFormRef = React.createRef();
        this.state = {
            loading: true,
        };
    }

    componentDidMount() {
        return this.populateState();
    }

    componentWillUnmount() {
        return this.setState = (state, callback) => { };
    }

    onViewTenant = (id) => {
        this.companyFormRef.current.open(id); this.setState({ showTenantForm: true });
    };

    onViewTheme = (id) => {
        this.tenantThemeFormRef.current.open(id); this.setState({ showTenantThemeForm: true });
    };
    
    onTenantFormClosed = () => {
        this.setState({ showTenantForm: false });
        this.setState({ showTenantThemeForm: false });
        if (this.state.gridOptions) {
            this.state.gridOptions.refresh();
        }
    };

    populateState = async () => {
        const [isAuthenticated] = await Promise.all([authService.isAuthenticated(), authService.getUser()]);

        if (isAuthenticated) {
            const [usStates] = await Promise.all([
                util.fetch.js(ApiRoutes.typeAheads.USStates()),
            ]);

            const gridOptions = createGridOptions(this);

            gridOptions.components = {
                selectFilter: DataGridSelectFilter,
                selectFloatingFilter: DataGridSelectFloatingFilter,
                iconRenderer: IconCellRenderer,
                nameRenderer: LinkCellRenderer,
                linkIconRenderer: LinkIconRenderer,
            };

            gridOptions.columnDefs = [
                {
                    flex: 0,
                    width: 80,
                    headerName: '',
                    valueGetter: 'node.id',
                    sortable: false,
                    cellRenderer: indexCellRenderer,
                },
                {
                    colId: 'CompanyName',
                    sortable: true,
                    headerName: 'Name',
                    field: 'name',
                    filter: agGridConstants.columnFilterTypes.text,
                    floatingFilter: true,
                    filterParams: TextFilterDefaults,
                    sort: { direction: 'asc', priority: 0 },
                    cellRenderer: 'nameRenderer',
                    cellRendererParams: {
                        clicked: this.onViewTenant,
                        nameField: 'name',
                        idField: 'id',
                        title: 'View this Tenant',
                    },
                },
                {
                    colId: 'Id',
                    flex: 0,    
                    width: 20, 
                    headerName: 'Theme',
                    sortable: false,
                    cellRenderer: 'iconRenderer',
                    cellRendererParams: {
                        clicked: this.onViewTheme,
                        idField: 'id',
                        iconClass: 'fa-palette',
                        nameField: 'Ho',
                    },
                },
                {
                    flex: 0,
                    colId: 'Settings',
                    sortable: false,
                    headerName: 'Settings',
                    cellRenderer: 'linkIconRenderer',
                    cellRendererParams: {
                        to: (id) => `${AppNavPaths.Tenant}/${id}`,
                        idField: 'id',
                        title: 'View this Tenant Settings',
                        iconClass: faCog,
                    },
                },
                {
                    colId: 'Location.Address1',
                    sortable: false,
                    headerName: 'Address Line 1',
                    field: 'address1',
                    filter: agGridConstants.columnFilterTypes.text,
                    filterParams: TextFilterDefaults,
                    floatingFilter: true,
                },
                {
                    colId: 'Location.Address2',
                    sortable: false,
                    headerName: 'Address Line 2',
                    field: 'address2',
                    filter: agGridConstants.columnFilterTypes.text,
                    filterParams: TextFilterDefaults,
                    floatingFilter: true,
                },
                {
                    colId: 'Location.City',
                    sortable: true,
                    headerName: 'City',
                    field: 'city',
                    filter: agGridConstants.columnFilterTypes.text,
                    filterParams: TextFilterDefaults,
                    floatingFilter: true,
                },
                {
                    flex: 0,
                    width: 140,
                    colId: 'Location.State',
                    sortable: true,
                    headerName: 'State',
                    field: 'state',
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: {
                        labelText: 'Filter by State',
                        options: usStates,
                        optionsLabel: 'label',
                        optionsValue: 'value',
                    },
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                        options: usStates,
                        optionsLabel: 'label',
                        optionsValue: 'value',
                    },
                },
                {
                    flex: 0,
                    width: 160,
                    colId: 'Location.Zip',
                    sortable: true,
                    headerName: 'Zip',
                    field: 'zip',
                    filter: agGridConstants.columnFilterTypes.text,
                    filterParams: TextFilterDefaults,
                    floatingFilter: true,
                },
                {
                    colId: 'Location.Phone',
                    sortable: true,
                    headerName: 'Phone',
                    field: 'phone',
                    filter: agGridConstants.columnFilterTypes.text,
                    filterParams: TextFilterDefaults,
                    floatingFilter: true,
                },
            ];



            const ds = createDataSource(ApiRoutes.tenant.search(), gridOptions);

            this.setState({
                isAuthenticated,
                loading: false,
                gridOptions,
                dataSource: ds,
            });
        } else {
            // redirect to access denied?
        }
    };

    render() {
        const { isAuthenticated, rowData, gridOptions } = this.state;
        if (!!this.state.loading || !isAuthenticated || !this.context.user) return (<Progress />);
        if (!isAuthenticated || !this.context.user.isAdmin) return ''; // probably redundant
        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon icon={faWarehouse} className="mr-2 text-muted" />
                    <span>Tenants</span>
                </PageHeading>
                <DataGridToolbar
                    entity="company"
                    gridApi={this.state.gridApi}
                    dataSource={this.state.dataSource}
                    onAdd={() => {
                        this.setState({ showTenantForm: true });
                        if (this.companyFormRef.current) {
                            this.companyFormRef.current.open();
                        }
                    }}
                    addLabel="Add Tenant"
                />
                <DataGrid
                    domLayout="normal"
                    rowData={rowData}
                    gridOptions={gridOptions}
                    gridStatus={this.state.gridStatus}

                />

                <ManageTenant
                    show={this.state.showTenantForm}
                    toggleShow={(show) => this.setState({ showTenantForm: show })}
                    ref={this.companyFormRef}
                    onClose={this.onTenantFormClosed}
                />
                <ManageTenantTheme
                    show={this.state.showTenantThemeForm}
                    toggleShow={(show) => this.setState({ showTenantThemeForm: show })}
                    ref={this.tenantThemeFormRef}
                    onClose={this.onTenantFormClosed}
                />
            </PageWrap>
        );
    }
}
export default withRouter(TenantIndex);
